import cloneDeep from "lodash/cloneDeep";
import { INSIGHTS_LENS_SLUGS } from "../../../../../constants/default-insights-lens";
import type {
  BodyPartKey,
  ClientConfiguration,
  SectionKey,
  SectionMode,
  TrackerKey,
  WeekdayIndex,
} from "../../../../../types/clientConfig";

export const DEFAULT_TEMPLATE_TYPES = ["default_pull", "default_push"];

export { INSIGHTS_LENS_SLUGS };

export type InsightsLensSlug = (typeof INSIGHTS_LENS_SLUGS)[number];

/**
 * Weekday indices: 0 = Sun … 6 = Sat (matches `Date#getDay`/msb-mobile's `selectedDays` /
 * `getClientConfigWeekdayIndexSun0Sat6`). This is the canonical, storage-level order — display
 * order in the picker instead follows the athlete's `startingCalendarDay` (see
 * `rotateWeekdayDisplayOrder`); `FULL_WEEK` itself never changes.
 */
export const FULL_WEEK: WeekdayIndex[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * Canonical weekday indices (0 = Sun … 6 = Sat) rotated to display order starting at
 * `startingCalendarDay` — same rotation rule as `parts/inputs/select-days.js`'s
 * `getOrderedDayKeys`. Purely a render-order helper: values in `schedule` are never touched.
 */
export const rotateWeekdayDisplayOrder = (
  startingCalendarDay: number = 0
): WeekdayIndex[] => {
  const start = (((Number(startingCalendarDay) || 0) % 7) + 7) % 7;
  return FULL_WEEK.map((_, i) => ((start + i) % 7) as WeekdayIndex);
};

/** All valid body part keys for `bodyMeasurements` (matches API `BODY_PART_KEYS`) */
export const BODY_PART_KEYS: BodyPartKey[] = [
  "Neck",
  "Chest",
  "Left Arm",
  "Right Arm",
  "Waist",
  "Hips",
  "Left Thigh",
  "Right Thigh",
  "Left Calf",
  "Right Calf",
  "Glutes",
];

export const TRACKER_KEYS: TrackerKey[] = [
  "readiness",
  "sessionRpe",
  "bodyWeight",
  "bodyFat",
  "bodyMeasurements",
  "progressPhotos",
  "mealPhotos",
  "sleep",
  "steps",
  "water",
  "hrv",
  "restingHeartRate",
];

/**
 * Copy of `config` with the readiness `forced` flag set; adds the readiness item when the config
 * has none yet. `config` is never mutated.
 */
export const withReadinessForced = (
  config: ClientConfiguration,
  forced: boolean
): ClientConfiguration => {
  const next = cloneDeep(config);
  const { items } = next.sections.tracking;
  if (!items.readiness) {
    items.readiness = {
      coachRequired: false,
      clientEnabled: false,
      order: TRACKER_KEYS.indexOf("readiness"),
    };
  }
  items.readiness.forced = forced;
  return next;
};

export const SECTION_KEYS: SectionKey[] = [
  "training",
  "nutrition",
  "mealPlans",
  "dailyAccountabilities",
  "goals",
  "femaleCycleTracking",
];

interface TrackerRowConfig {
  key: TrackerKey;
  icon: string;
  titleKey: string;
  descKey: string;
}

export const SYNCABLE_TRACKER_KEYS: TrackerKey[] = [
  "bodyWeight",
  "steps",
  "sleep",
  "hrv",
  "restingHeartRate",
];

export const isSyncableTracker = (key: TrackerKey): boolean =>
  SYNCABLE_TRACKER_KEYS.includes(key);

export const TRACKER_ROWS: TrackerRowConfig[] = [
  {
    key: "readiness",
    icon: "readiness-bolt-heart.png",
    titleKey: "clientConfiguration.trackers.readiness.title",
    descKey: "clientConfiguration.trackers.readiness.desc",
  },
  {
    key: "sessionRpe",
    icon: "bar-chart.png",
    titleKey: "clientConfiguration.trackers.sessionRpe.title",
    descKey: "clientConfiguration.trackers.sessionRpe.desc",
  },
  {
    key: "bodyWeight",
    icon: "scale-solid.png",
    titleKey: "clientConfiguration.trackers.bodyWeight.title",
    descKey: "clientConfiguration.trackers.bodyWeight.desc",
  },
  {
    key: "progressPhotos",
    icon: "camera-solid.png",
    titleKey: "clientConfiguration.trackers.progressPhotos.title",
    descKey: "clientConfiguration.trackers.progressPhotos.desc",
  },
  {
    key: "mealPhotos",
    icon: "pizza-slice-solid.svg",
    titleKey: "clientConfiguration.trackers.mealPhotos.title",
    descKey: "clientConfiguration.trackers.mealPhotos.desc",
  },
  {
    key: "bodyFat",
    icon: "body-fat-solid.svg",
    titleKey: "clientConfiguration.trackers.bodyFat.title",
    descKey: "clientConfiguration.trackers.bodyFat.desc",
  },
  {
    key: "bodyMeasurements",
    icon: "body-solid.svg",
    titleKey: "clientConfiguration.trackers.bodyMeasurements.title",
    descKey: "clientConfiguration.trackers.bodyMeasurements.desc",
  },
  {
    key: "sleep",
    icon: "bed-solid.svg",
    titleKey: "clientConfiguration.trackers.sleep.title",
    descKey: "clientConfiguration.trackers.sleep.desc",
  },
  {
    key: "steps",
    icon: "person-walking-solid.svg",
    titleKey: "clientConfiguration.trackers.steps.title",
    descKey: "clientConfiguration.trackers.steps.desc",
  },
  {
    key: "water",
    icon: "drop-icon-solid.svg",
    titleKey: "clientConfiguration.trackers.water.title",
    descKey: "clientConfiguration.trackers.water.desc",
  },
  {
    key: "hrv",
    icon: "heart-pulse-solid.svg",
    titleKey: "clientConfiguration.trackers.hrv.title",
    descKey: "clientConfiguration.trackers.hrv.desc",
  },
  {
    key: "restingHeartRate",
    icon: "heart-solid.svg",
    titleKey: "clientConfiguration.trackers.restingHeartRate.title",
    descKey: "clientConfiguration.trackers.restingHeartRate.desc",
  },
];

interface SectionRadioOptionConfig {
  value: SectionMode;
  labelKey: string;
  descKey: string;
}

interface SectionRadioConfig {
  key: SectionKey;
  titleKey: string;
  icon: string;
  options: SectionRadioOptionConfig[];
  emoji: string;
}

export type WidgetDependentSectionKey = "mealPlans" | "dailyAccountabilities";

export interface ClientConfigWidgetAvailability {
  mealPlans: boolean;
  dailyAccountabilities: boolean;
}

export function getSectionWidgetAvailability(
  availability: ClientConfigWidgetAvailability = {
    mealPlans: false,
    dailyAccountabilities: false,
  }
): Record<WidgetDependentSectionKey, boolean> {
  return {
    mealPlans: availability.mealPlans,
    dailyAccountabilities: availability.dailyAccountabilities,
  };
}

export function isSectionDisabledByMissingWidget(
  sectionKey: SectionKey,
  availability: Record<WidgetDependentSectionKey, boolean>
): boolean {
  if (sectionKey === "mealPlans") return !availability.mealPlans;
  if (sectionKey === "dailyAccountabilities") {
    return !availability.dailyAccountabilities;
  }
  return false;
}

export function getEffectiveSectionMode(
  mode: SectionMode | undefined,
  options: { isClientOverride?: boolean; defaultMode?: SectionMode } = {}
): SectionMode {
  if (options.isClientOverride) return "client";
  return mode ?? options.defaultMode ?? "coach";
}

/** Client-library option (`client` mode) when the required pull widget is not enabled. */
export function isClientOptionBlockedByMissingWidget(
  sectionKey: SectionKey,
  optionValue: SectionMode,
  availability: Record<WidgetDependentSectionKey, boolean>
): boolean {
  return (
    optionValue === "client" &&
    isSectionDisabledByMissingWidget(sectionKey, availability)
  );
}

export function getWidgetRequiredMessageKey(
  sectionKey: SectionKey
):
  | "clientConfiguration.widgetRequired.mealPlans"
  | "clientConfiguration.widgetRequired.dailyAccountabilities"
  | null {
  if (sectionKey === "mealPlans") {
    return "clientConfiguration.widgetRequired.mealPlans";
  }
  if (sectionKey === "dailyAccountabilities") {
    return "clientConfiguration.widgetRequired.dailyAccountabilities";
  }
  return null;
}

export function getActiveClientNoWidgetMessageKey(
  sectionKey: SectionKey
):
  | "clientConfiguration.widgetRequired.activeClientNoWidget.mealPlans"
  | "clientConfiguration.widgetRequired.activeClientNoWidget.dailyAccountabilities"
  | null {
  if (sectionKey === "mealPlans") {
    return "clientConfiguration.widgetRequired.activeClientNoWidget.mealPlans";
  }
  if (sectionKey === "dailyAccountabilities") {
    return "clientConfiguration.widgetRequired.activeClientNoWidget.dailyAccountabilities";
  }
  return null;
}

export function getWidgetPageHref(sectionKey: SectionKey): string | null {
  if (sectionKey === "mealPlans") {
    return "/coaching-portal/widgets?section=learn&learn=meal-plan-library";
  }
  if (sectionKey === "dailyAccountabilities") {
    return "/coaching-portal/widgets?section=learn&learn=daily-accountability-template";
  }
  return null;
}

export function getCoachNoDataMessageKey(
  sectionKey: SectionKey
):
  | "clientConfiguration.coachNoData.nutrition"
  | "clientConfiguration.coachNoData.mealPlans"
  | "clientConfiguration.coachNoData.dailyAccountabilities"
  | null {
  if (sectionKey === "nutrition") {
    return "clientConfiguration.coachNoData.nutrition";
  }
  if (sectionKey === "mealPlans") {
    return "clientConfiguration.coachNoData.mealPlans";
  }
  if (sectionKey === "dailyAccountabilities") {
    return "clientConfiguration.coachNoData.dailyAccountabilities";
  }
  return null;
}

export const SECTION_RADIO_CONFIG: SectionRadioConfig[] = [
  {
    key: "nutrition",
    titleKey: "clientConfiguration.sections.nutrition",
    icon: "vuesax-linear-heart-add.svg",
    emoji: "🥗",
    options: [
      {
        value: "coach",
        labelKey: "clientConfiguration.sectionOptions.nutrition.coach.label",
        descKey: "clientConfiguration.sectionOptions.nutrition.coach.desc",
      },
      {
        value: "client",
        labelKey: "clientConfiguration.sectionOptions.nutrition.client.label",
        descKey: "clientConfiguration.sectionOptions.nutrition.client.desc",
      },
      {
        value: "hidden",
        labelKey: "clientConfiguration.sectionOptions.nutrition.hidden.label",
        descKey: "clientConfiguration.sectionOptions.nutrition.hidden.desc",
      },
    ],
  },
  {
    key: "mealPlans",
    titleKey: "clientConfiguration.sections.mealPlans",
    icon: "Pizza.svg",
    emoji: "📋",
    options: [
      {
        value: "coach",
        labelKey: "clientConfiguration.sectionOptions.mealPlans.coach.label",
        descKey: "clientConfiguration.sectionOptions.mealPlans.coach.desc",
      },
      {
        value: "client",
        labelKey: "clientConfiguration.sectionOptions.mealPlans.client.label",
        descKey: "clientConfiguration.sectionOptions.mealPlans.client.desc",
      },
      {
        value: "hidden",
        labelKey: "clientConfiguration.sectionOptions.mealPlans.hidden.label",
        descKey: "clientConfiguration.sectionOptions.mealPlans.hidden.desc",
      },
    ],
  },
  {
    key: "dailyAccountabilities",
    titleKey: "clientConfiguration.sections.dailyAccountabilities",
    icon: "vuesax-linear-tick-circle.svg",
    emoji: "✅",
    options: [
      {
        value: "coach",
        labelKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.coach.label",
        descKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.coach.desc",
      },
      {
        value: "client",
        labelKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.client.label",
        descKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.client.desc",
      },
      {
        value: "hidden",
        labelKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.hidden.label",
        descKey:
          "clientConfiguration.sectionOptions.dailyAccountabilities.hidden.desc",
      },
    ],
  },
];

/** Maps API body part labels to i18n keys under `clientConfiguration.bodyParts`. */
export const BODY_PART_I18N_KEYS: Record<BodyPartKey, string> = {
  Neck: "neck",
  Chest: "chest",
  "Left Arm": "leftArm",
  "Right Arm": "rightArm",
  Waist: "waist",
  Hips: "hips",
  "Left Thigh": "leftThigh",
  "Right Thigh": "rightThigh",
  "Left Calf": "leftCalf",
  "Right Calf": "rightCalf",
  Glutes: "glutes",
};

export const sortScheduleDays = (schedule: WeekdayIndex[]): WeekdayIndex[] => {
  if (!schedule || !Array.isArray(schedule)) return schedule;
  return [...schedule].sort((a, b) => a - b);
};

export const schedulesEqual = (
  scheduleA: WeekdayIndex[],
  scheduleB: WeekdayIndex[]
): boolean => {
  return (
    JSON.stringify(sortScheduleDays(scheduleA)) ===
    JSON.stringify(sortScheduleDays(scheduleB))
  );
};
